import Form, { GroupItem, Label, SimpleItem } from 'devextreme-react/form';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import ScrollView from 'devextreme-react/scroll-view';
import { LoadPanel } from 'devextreme-react/ui/load-panel';
import { EmailRule, RequiredRule } from 'devextreme-react/validator';
import notify from 'devextreme/ui/notify';
import { array, bool, func, object } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import API from '../../../../services/API';

const EditExternalContactPopup = ({
  areas,
  phoneTypes,
  categories,
  isVisible,
  setIsVisible,
  updateContactGrid,
  currentContact,
}) => {
  const [phoneOptions, setPhoneOptions] = useState([]);
  const [currentAreaId, setCurrentAreaId] = useState(
    currentContact?.Contact?.AreaId
  );
  const [categoriesTags, setCategoriesTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (currentContact && currentContact.Phones) {
      currentContact.Phones.forEach((item) => {
        item.PhoneNumber = item.PhoneNumber.replaceAll('(', '')
          .replaceAll(')', '')
          .replaceAll(' ', '')
          .replaceAll('-', '');
      });
    }
    getPhoneOptions(currentContact);
  }, []); //eslint-disable-line

  useEffect(() => {
    setCategoriesTags(
      categories.filter((category) => category.AreaId === currentAreaId)
    );
  }, [currentAreaId]); //eslint-disable-line

  const bottomToolbarButtons = [
    {
      text: 'OK',
      type: 'success',
      onClick: () => {
        handleSaveOnClick();
      },
      // disabled: importButtonDisabled,
    },
    {
      text: 'Cancel',
      type: 'normal',
      onClick: () => {
        setIsVisible(false);
      },
    },
  ];

  const addPhoneButtonOptions = {
    icon: 'add',
    text: 'Add Phone',
    onClick: () => {
      if (!currentContact.Phones) {
        currentContact.Phones = [];
      }
      const phonesOrder = currentContact.Phones.map(
        (phone) => phone.ContactPhoneOrder
      );

      const maxPhoneOrder = phonesOrder.reduce(
        (acc, current) => Math.max(acc, current),
        0
      );

      currentContact.Phones.push({
        ContactId: currentContact.Contact.ContactId,
        PhoneTypeId: 0,
        ContactPhoneOrder: maxPhoneOrder + 1,
        PhoneNumber: '',
        // Preferred: maxPhoneOrder === 0 ? true : false,
      });
      getPhoneOptions(currentContact);
    },
  };

  const getPhoneOptions = (contact) => {
    const options = [];

    if (contact && contact.Phones) {
      for (let i = 0; i < contact.Phones.length; i++) {
        options.push(generateNewPhoneOptions(contact.Phones[i], i));
      }
    }

    setPhoneOptions(options);

    return options;
  };

  const generateNewPhoneOptions = (phone, index) => ({
    maxLength: 50,
    mode: 'Tel',
    mask: '(000) 000-0000',

    buttons: [
      {
        name: `trash`,
        location: 'after',
        options: {
          stylingMode: 'text',
          icon: 'trash',
          onClick: () => {
            currentContact.Phones.splice(index, 1);
            getPhoneOptions(currentContact);
          },
        },
      },
    ],
  });

  async function handleSaveOnClick() {
    setSaving(true);

    try {
      if (formRef.current.instance.validate().isValid) {
        if (
          !currentContact.Phones ||
          (currentContact.Phones && currentContact.Phones.length <= 0)
        ) {
          throw new Error('Please, inform at least one phone number.');
        }

        const res = await API.sendRequest(
          'Contact/SaveComplete',
          'POST',
          currentContact,
          true
        );

        if (res.status === 200) {
          updateContactGrid();
          notify('Contact saved', 'success', 2000);
          setIsVisible(false);
        }
      } else {
        setSaving(false);
        notify(`Please check the highlighted fields.`, 'error', 2000);
      }
    } catch (e) {
      setSaving(false);
      notify(`Error: ${e.message}`, 'error', 2000);
    }
  }

  function handleAddNewTag(args) {
    args.customItem = {
      CategoryDesc: args.text,
      AreaId: currentAreaId,
    };

    setCategoriesTags((prevState) => [...prevState, args.customItem]);
  }
  return (
    <Popup
      visible={isVisible}
      dragEnabled
      showCloseButton={false}
      showTitle={!!currentContact.Contact.ContactId}
      title={
        currentContact && currentContact.Contact.DisplayName
          ? currentContact.Contact.DisplayName
          : `${currentContact.Contact.FirstName} ${currentContact.Contact.LastName}`
      }
      height={625}
    >
      {bottomToolbarButtons.map((buttonOptions, index) => (
        <ToolbarItem
          key={index}
          options={{ stylingMode: 'contained', ...buttonOptions }}
          widget="dxButton"
          toolbar="bottom"
          location="after"
        />
      ))}
      <LoadPanel
        shadingColor="rgba(255,255,255,0.4)"
        visible={saving}
        message="Saving..."
        closeOnOutsideClick={false}
      />
      <ScrollView width="100%" height="100%">
        <Form
          id="form"
          formData={currentContact}
          showColonAfterLabel
          labelLocation="left"
          colCount={2}
          ref={formRef}
          style={{
            paddingRight: '10px',
            paddingLeft: '10px',
          }}
        >
          <GroupItem caption="Information">
            <SimpleItem dataField="Contact.Prefix" editorType="dxTextBox">
              <Label text="Prefix" />
            </SimpleItem>
            <SimpleItem dataField="Contact.FirstName" editorType="dxTextBox">
              <RequiredRule />
              <Label text="First Name" />
            </SimpleItem>
            <SimpleItem dataField="Contact.LastName" editorType="dxTextBox">
              <Label text="Last Name" />
            </SimpleItem>
            <SimpleItem dataField="Contact.Company" editorType="dxTextBox">
              <Label text="Company" />
            </SimpleItem>
            <SimpleItem
              dataField="Contact.AreaId"
              editorType="dxSelectBox"
              editorOptions={{
                items: areas,
                displayExpr: 'AreaDesc',
                valueExpr: 'AreaId',
                onValueChanged: ({ value }) => {
                  setCurrentAreaId(value);
                },
              }}
            >
              <Label text="Area" />
            </SimpleItem>
            <SimpleItem
              editorType="dxTagBox"
              dataField="Category"
              editorOptions={{
                items: categoriesTags,
                displayExpr: 'CategoryDesc',
                acceptCustomValue: true,
                searchEnabled: true,
                hideSelectedItems: true,
                onCustomItemCreating: handleAddNewTag,
              }}
            >
              <Label text="Categories" />
            </SimpleItem>
            <SimpleItem dataField="Contact.JobTitle" editorType="dxTextBox">
              <Label text="Job Title" />
            </SimpleItem>
            <SimpleItem dataField="Contact.Description" editorType="dxTextBox">
              <Label text="Description" />
            </SimpleItem>
            <GroupItem colCount={2}>
              <SimpleItem dataField="Contact.Active" editorType="dxCheckBox">
                <Label text="Active" />
              </SimpleItem>
              <SimpleItem
                dataField="Contact.IsEMRContact"
                editorType="dxCheckBox"
              >
                <Label text="Okta Tile Contact" />
              </SimpleItem>
            </GroupItem>
          </GroupItem>
          <GroupItem caption="Contacts">
            <SimpleItem dataField="Contact.Email" editorType="dxTextBox">
              <EmailRule message="Email is invalid" />
              <Label text="Email" />
            </SimpleItem>
            <GroupItem colCount={1} caption="Phones">
              {phoneOptions.map((phone, index) => (
                <GroupItem
                  colCount={2}
                  key={`PhoneItem${index}`}
                  itemType="group"
                >
                  <SimpleItem
                    key={`PhoneType${index}`}
                    dataField={`Phones[${index}].PhoneTypeId`}
                    editorType="dxSelectBox"
                    editorOptions={{
                      items: phoneTypes,
                      searchEnabled: true,
                      displayExpr: 'PhoneTypeDesc',
                      valueExpr: 'PhoneTypeId',
                    }}
                  >
                    <RequiredRule />
                    <Label text="Type" visible={false} />
                  </SimpleItem>
                  <SimpleItem
                    key={`Phone${index}`}
                    dataField={`Phones[${index}].PhoneNumber`}
                    editorOptions={phone}
                  >
                    <RequiredRule />
                    <Label text="Phone" visible={false} />
                  </SimpleItem>
                </GroupItem>
              ))}
              <SimpleItem
                itemType="button"
                horizontalAlignment="left"
                cssClass="add-phone-button"
                buttonOptions={addPhoneButtonOptions}
              />
            </GroupItem>
          </GroupItem>
        </Form>
      </ScrollView>
    </Popup>
  );
};

export default EditExternalContactPopup;

EditExternalContactPopup.propTypes = {
  areas: array,
  phoneTypes: array,
  categories: array,
  isVisible: bool.isRequired,
  updateContactGrid: func.isRequired,
  setIsVisible: func.isRequired,
  currentContact: object.isRequired,
};

EditExternalContactPopup.defaultProps = {
  categories: [],
  areas: [],
  phoneTypes: [],
};
